import { useRef, useState } from 'react';
import { Loader2, Mic, Paperclip, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { uploadSceneAudioFromFile } from '@/lib/narrationImageStoryUpload';
import { mediaUrlForSegmentField } from '@/lib/mediaUrls';
import { toast } from 'sonner';

const ACCEPT = 'audio/*,.mp3,.wav,.m4a,.aac,.flac,.ogg,.webm,.mpeg,.mpg,.mpa';

/**
 * Attach or replace per-scene narration audio on /narration/create.
 */
export function NarrationSceneAudioAttach({
  scene,
  disabled = false,
  dark = false,
  onAudioChange,
  className = '',
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const audioUrl = scene?.audio_url ? mediaUrlForSegmentField(scene, 'audio_url') : null;
  const duration = Number(scene?.audio_duration || scene?.duration) || 0;
  const label = scene?.attached_audio_filename || scene?.source_filename || 'Attached audio';

  const handlePick = () => {
    if (disabled || busy) return;
    inputRef.current?.click();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || disabled) return;
    setBusy(true);
    try {
      const data = await uploadSceneAudioFromFile(file);
      onAudioChange?.(scene, {
        audio_url: data.audio_url || data.url || null,
        audio_duration: Number(data.audio_duration || data.duration) || 0,
        duration: Number(data.audio_duration || data.duration) || Number(scene?.duration) || 3,
        audio_duration_probed: Boolean(data.audio_duration_probed),
        user_attached_audio: true,
        tts_provider: 'upload',
        attached_audio_filename: file.name,
      });
      toast.success(`Audio attached to scene ${scene?.segment_number}`);
    } catch (err) {
      toast.error(err?.message || 'Could not upload scene audio');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = () => {
    if (disabled || busy) return;
    onAudioChange?.(scene, {
      audio_url: null,
      audio_duration: null,
      audio_duration_probed: false,
      user_attached_audio: false,
      tts_provider: null,
      attached_audio_filename: null,
    });
  };

  const btnClass = dark
    ? 'border-white/15 text-slate-100 hover:bg-white/10'
    : '';

  return (
    <div className={`space-y-1.5 ${className}`} data-testid={`scene-audio-attach-${scene?.segment_number}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`h-7 text-[10px] gap-1 px-2 ${btnClass}`}
          disabled={disabled || busy}
          onClick={handlePick}
          data-testid={`attach-scene-audio-btn-${scene?.segment_number}`}
        >
          {busy ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : audioUrl ? (
            <Paperclip className="w-3 h-3" />
          ) : (
            <Mic className="w-3 h-3" />
          )}
          {audioUrl ? 'Replace audio' : 'Attach audio'}
        </Button>
        {audioUrl ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={`h-7 text-[10px] px-2 ${dark ? 'text-red-300 hover:text-red-200' : 'text-red-500 hover:text-red-600'}`}
            disabled={disabled || busy}
            onClick={handleRemove}
            data-testid={`remove-scene-audio-btn-${scene?.segment_number}`}
          >
            <X className="w-3 h-3 mr-0.5" />
            Remove
          </Button>
        ) : null}
      </div>
      {audioUrl ? (
        <div className="space-y-1">
          <p className={`text-[10px] leading-relaxed truncate ${dark ? 'text-slate-300' : 'narration-wizard-hint'}`}>
            {label}
            {duration > 0 ? ` · ${duration.toFixed(1)}s` : ''}
          </p>
          <audio
            controls
            preload="metadata"
            src={audioUrl}
            className="w-full h-8 max-w-full"
            data-testid={`scene-audio-preview-${scene?.segment_number}`}
          />
          <p className={`text-[10px] leading-relaxed ${dark ? 'text-slate-400' : 'narration-wizard-hint'}`}>
            Pipeline uses this clip instead of TTS for this scene.
          </p>
        </div>
      ) : (
        <p className={`text-[10px] leading-relaxed ${dark ? 'text-slate-400' : 'narration-wizard-hint'}`}>
          Optional — upload MP3/WAV for this scene. Otherwise narration is generated from the script.
        </p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={handleFile}
        data-testid={`attach-scene-audio-input-${scene?.segment_number}`}
      />
    </div>
  );
}
